import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';

import { elGR as dataGridElGR, enUS as dataGridEnUS } from '@mui/x-data-grid/locales';
import { elGR as coreElGR, enUS as coreEnUS } from '@mui/material/locale';
import { elGR as pickersElGR, enUS as pickersEnUS } from '@mui/x-date-pickers-pro/locales';

declare module '@mui/material/styles' {
  interface Palette {
    cancel: Palette['primary'];
    highlightField: Palette['primary'];
    black: Palette['primary'];
    blackGrey: Palette['primary'];
    mediumGrey: Palette['primary'];
    secondaryText: Palette['primary'];
    filterGrey: Palette['primary'];
    overlayGrey: Palette['primary'];
    orderGrey: Palette['primary'];
    orderYellow: Palette['primary'];
    orderRed: Palette['primary'];
    tooltipGrey: Palette['primary'];
    shiftsGreen: Palette['primary'];
    breakGrey: Palette['primary'];
    currentDayBlue: Palette['primary'];
    locationBlue: Palette['primary'];
    locationBlueBg: Palette['primary'];
    chartPrimary: Palette['primary'];
    chartPrimaryLight: Palette['primary'];
  }
  interface PaletteOptions {
    cancel: PaletteOptions['primary'];
    highlightField: PaletteOptions['primary'];
    black: PaletteOptions['primary'];
    blackGrey: PaletteOptions['primary'];
    mediumGrey: PaletteOptions['primary'];
    secondaryText: PaletteOptions['primary'];
    filterGrey: PaletteOptions['primary'];
    overlayGrey: PaletteOptions['primary'];
    orderGrey: PaletteOptions['primary'];
    orderYellow: PaletteOptions['primary'];
    orderRed: PaletteOptions['primary'];
    tooltipGrey: PaletteOptions['primary'];
    shiftsGreen: PaletteOptions['primary'];
    breakGrey: PaletteOptions['primary'];
    currentDayBlue: PaletteOptions['primary'];
    locationBlue: PaletteOptions['primary'];
    locationBlueBg: PaletteOptions['primary'];
    chartPrimary: PaletteOptions['primary'];
    chartPrimaryLight: PaletteOptions['primary'];
  }
}

// Extend color prop on components
declare module '@mui/material/Button' {
  export interface ButtonPropsColorOverrides {
    cancel: true;
    filterGrey: true;
  }
}

declare module '@mui/material/IconButton' {
  export interface IconButtonPropsColorOverrides {
    filterGrey: true;
  }
}

declare module '@mui/material/Chip' {
  export interface ChipPropsColorOverrides {
    orderGrey: true;
    orderYellow: true;
    orderRed: true;
  }
}

// A custom theme for this app — base (no locale applied)
const baseTheme = createTheme({
  palette: {
    primary: {
      main: '#6358E6',
      contrastText: '#fff',
    },
    secondary: {
      main: '#FC6D43',
      contrastText: '#fff',
    },
    secondaryText: {
      main: '#757575',
    },
    error: {
      main: '#FF1010',
    },
    cancel: {
      main: '#DFE3E6',
    },
    highlightField: {
      main: '#F3F5F7',
    },
    black: {
      main: '#000000',
    },
    blackGrey: {
      main: '#000000DE',
    },
    overlayGrey: {
      main: '#DFE3E6DE',
    },
    mediumGrey: {
      main: '#AAAAAA',
    },
    filterGrey: {
      main: '#707D8A',
      contrastText: '#fff',
    },
    tooltipGrey: {
      main: '#0000008A',
    },
    orderGrey: {
      main: '#F3F5F7',
      contrastText: '#000',
    },
    orderYellow: {
      main: '#FFFBD9',
      contrastText: '#000',
    },
    orderRed: {
      main: '#FFEAEA',
      contrastText: '#000',
    },
    shiftsGreen: {
      main: '#8FDF82',
      contrastText: '#000',
    },
    breakGrey: {
      main: '#AAA',
      contrastText: '#000',
    },
    currentDayBlue: {
      main: '#EAF6FF',
    },
    locationBlue: {
      main: '#1E88E5',
    },
    locationBlueBg: {
      main: '#E0F4FF',
    },
    chartPrimary: {
      main: '#6358E6',
    },
    chartPrimaryLight: {
      main: '#C7C2F2',
    },
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
        paper: {
          borderRadius: '8px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#F3F5F7',
            '&:hover': {
              backgroundColor: '#F3F5F7',
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          padding: '0',
          minHeight: '52px',
          '@media (min-width: 600px)': {
            minHeight: '52px',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          color: '#000000DE',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          minHeight: '52px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'inherit',
          '&.MuiButton-contained.Mui-disabled': {
            backgroundColor: '#DFE3E6',
            color: '#707D8A',
            opacity: 1,
          },
        },
      },
      variants: [
        {
          props: { color: 'cancel' },
          style: {
            color: '#000000',
          },
        },
      ],
    },
    MuiChip: {},
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-container--top [role=row]': {
            background: '#F3F5F7',
          },
          '& .MuiDataGrid-container--top .MuiDataGrid-headerFilterRow': {
            background: '#F3F5F780',
          },
          '& .MuiTablePagination-root': {
            color: '#707D8A',
          },
          '& .MuiInputLabel-standard': {
            color: '#707D8A',
          },
          border: 'none',
        },
        row: {
          backgroundColor: '#fff',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'inherit',
        },
      },
    },
  },
});

const MUI_LOCALES = {
  el: { dataGrid: dataGridElGR, core: coreElGR, pickers: pickersElGR },
  en: { dataGrid: dataGridEnUS, core: coreEnUS, pickers: pickersEnUS },
} as const;

type ThemeLanguage = keyof typeof MUI_LOCALES;

export const getLocalizedTheme = (language: string) => {
  const key: ThemeLanguage = language in MUI_LOCALES ? (language as ThemeLanguage) : 'el';
  const { dataGrid, core, pickers } = MUI_LOCALES[key];
  return createTheme(baseTheme, dataGrid, core, pickers);
};

const theme = getLocalizedTheme('el');

export default theme;
